import { defineStore } from 'pinia'
import { ref } from 'vue'
import { sessionStorage } from '@shared/helpers/storage.js'
import { PublicApi } from '@shared/api-client'
import { getTimezone } from '@shared/utils'
import LandingPage from '@shared/data/models/landingPage.js'
import useUserStore from '@shared/stores/useUserStore.js'

export const useLandingPageStore = defineStore(
  'landingPage',
  () => {
    /** @type {import('vue').Ref<LandingPage|null>} */
    const landingPage = ref(null)

    function setLandingPage(payload) {
      landingPage.value = payload ? new LandingPage(payload) : null
    }

    function clearLandingPage() {
      landingPage.value = null
    }

    async function ensureLandingPage(slug, { forceReload = false } = {}) {
      if (!slug) {
        clearLandingPage()
        return null
      }

      const normalizedSlug = `${slug}`.toLowerCase()

      if (
        !forceReload &&
        landingPage.value?.slug === normalizedSlug &&
        landingPage.value
      ) {
        return landingPage.value
      }

      try {
        const response = await PublicApi.get(
          `v2/landing_pages/${normalizedSlug}`,
          {
            params: {
              language: useUserStore().lang,
              timezone: getTimezone(),
            },
          },
        )
        setLandingPage(response.data)
        return landingPage.value
      } catch {
        clearLandingPage()
        return null
      }
    }

    return {
      landingPage,
      setLandingPage,
      clearLandingPage,
      ensureLandingPage,
    }
  },
  {
    persist: {
      storage: sessionStorage,
      serializer: {
        serialize: (state) => {
          const rawState = { ...state }
          if (rawState.landingPage) {
            rawState.landingPage = rawState.landingPage._json
          }
          return JSON.stringify(rawState)
        },
        deserialize: (value) => {
          const parsed = JSON.parse(value)
          if (parsed.landingPage) {
            parsed.landingPage = new LandingPage(parsed.landingPage)
          }
          return parsed
        },
      },
    },
  },
)

export default useLandingPageStore
